/**
 * DataTable — Core configurable table using TanStack React Table.
 * Supports column resizing, sorting, and custom cell renderers.
 */
import React, { useMemo } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    flexRender,
} from '@tanstack/react-table';
import { ArrowUp, ArrowDown, ArrowUpDown, GripVertical } from 'lucide-react';
import { renderCell } from './CellRenderers';

export default function DataTable({
    data = [],
    visibleColumns = [],
    sorting = [],
    onSortingChange,
    onRowClick,
    columnSizing = {},
    onColumnSizingChange,
    className = '',
    // Selection support
    selectable = false,
    selectedIds = new Set(),
    onSelectionChange,
    idField = 'id',
}) {
    // Build TanStack column definitions from visible columns
    const columns = useMemo(() => {
        const cols = [];

        // Checkbox column for selection
        if (selectable) {
            const allIds = data.map(row => row[idField]).filter(Boolean);
            const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.has(id));
            const someSelected = allIds.some(id => selectedIds.has(id)) && !allSelected;

            cols.push({
                id: '__select',
                header: () => (
                    <input
                        type="checkbox"
                        checked={allSelected}
                        ref={el => { if (el) el.indeterminate = someSelected; }}
                        onChange={(e) => {
                            if (e.target.checked) {
                                onSelectionChange?.(new Set(allIds));
                            } else {
                                onSelectionChange?.(new Set());
                            }
                        }}
                        className="w-4 h-4 rounded border-stone-300 text-stone-900 focus:ring-stone-900/20 cursor-pointer"
                    />
                ),
                cell: ({ row }) => {
                    const rowId = row.original[idField];
                    return (
                        <input
                            type="checkbox"
                            checked={selectedIds.has(rowId)}
                            onChange={(e) => {
                                e.stopPropagation();
                                const next = new Set(selectedIds);
                                if (e.target.checked) next.add(rowId);
                                else next.delete(rowId);
                                onSelectionChange?.(next);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 rounded border-stone-300 text-stone-900 focus:ring-stone-900/20 cursor-pointer"
                        />
                    );
                },
                size: 40,
                enableSorting: false,
                enableResizing: false,
            });
        }

        cols.push(...visibleColumns.map(col => ({
            id: col.key,
            accessorFn: (row) => {
                // Handle custom fields
                if (col.key.startsWith('cf_')) {
                    return row.custom_fields?.[col.key.slice(3)] || '';
                }
                // Handle computed contact name
                if (col.key === 'full_name') {
                    return `${row.first_name || ''} ${row.last_name || ''}`.trim();
                }
                return row[col.key];
            },
            header: ({ column }) => {
                const isSorted = column.getIsSorted();
                return (
                    <button
                        className="flex items-center gap-1 w-full text-left group"
                        onClick={column.getToggleSortingHandler()}
                    >
                        <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider truncate">
                            {col.label}
                        </span>
                        <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                            {isSorted === 'asc' ? <ArrowUp className="w-3 h-3 text-stone-900" /> :
                             isSorted === 'desc' ? <ArrowDown className="w-3 h-3 text-stone-900" /> :
                             <ArrowUpDown className="w-3 h-3 text-stone-300" />}
                        </span>
                    </button>
                );
            },
            cell: ({ row, getValue }) => renderCell(col.type, getValue(), row.original, col),
            size: col.width || col.defaultWidth || 150,
            enableSorting: true,
            enableResizing: true,
        })));
        return cols;
    }, [visibleColumns, selectable, selectedIds, data, idField, onSelectionChange]);

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            columnSizing,
        },
        onSortingChange,
        onColumnSizingChange,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
    });

    return (
        <div className={`overflow-auto bg-white rounded-xl border border-stone-200 ${className}`}>
            <table className="w-full" style={{ minWidth: table.getTotalSize() }}>
                <thead className="sticky top-0 z-10 bg-stone-50 border-b border-stone-200">
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th
                                    key={header.id}
                                    className="relative px-3 py-2.5 text-left select-none"
                                    style={{ width: header.getSize() }}
                                >
                                    {flexRender(header.column.columnDef.header, header.getContext())}

                                    {/* Resize handle */}
                                    <div
                                        onMouseDown={header.getResizeHandler()}
                                        onTouchStart={header.getResizeHandler()}
                                        className={`absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none
                                            hover:bg-stone-400 ${header.column.getIsResizing() ? 'bg-stone-500' : 'bg-transparent'}`}
                                    />
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length} className="text-center py-16 text-sm text-stone-400">
                                No records found
                            </td>
                        </tr>
                    ) : (
                        table.getRowModel().rows.map(row => (
                            <tr
                                key={row.id}
                                onClick={() => onRowClick?.(row.original)}
                                className={`border-b border-stone-100 hover:bg-stone-50 transition-colors cursor-pointer ${
                                    selectable && selectedIds.has(row.original[idField]) ? 'bg-stone-50' : ''
                                }`}
                            >
                                {row.getVisibleCells().map(cell => (
                                    <td
                                        key={cell.id}
                                        className="px-3 py-2.5"
                                        style={{ width: cell.column.getSize(), maxWidth: cell.column.getSize() }}
                                    >
                                        <div className="truncate">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </div>
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}
